// -----------------------------------------------------------------------------
// HubSidebarDetails
//
// The 'details' tab content for the CompletedTreatmentHub sidebar. Read-only
// treatment context (TreatmentContextHeader) + two light edit affordances:
//   1. "Change start date" — collapsed accordion → MUI X DateTimePicker. On
//      accept it dispatches updateTransitionTime with the same payload shape as
//      StartedDateField.jsx ({ timestamp: 'DD-MM-YYYY HH:mm:ss.0Z', lead, id,
//      state }). Carries a prominent reporting/analytics warning.
//   2. "Edit patient details" — deep-links to the lead detail page (one source
//      of truth; the edit form is NOT embedded here).
//
// treatmentId / treatmentStatus are resolved in the hub (resolveRowTreatment)
// and passed down so the transition update targets the right lead_treatment.
// -----------------------------------------------------------------------------
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import {
    Box, Typography, Button, Collapse, Stack,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import { dispatch } from 'src/redux/store';
import { updateTransitionTime } from 'src/redux/slices/lead';
import { updateError } from 'src/redux/slices/user';
import TreatmentContextHeader from './TreatmentContextHeader';

export default function HubSidebarDetails({ row, leadId, practiceId, treatmentId, treatmentStatus, handleClose }) {
    const navigate = useNavigate();
    const [showDateEditor, setShowDateEditor] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [dateValue, setDateValue] = useState(null);

    const canEditDate = leadId != null && treatmentId != null && treatmentStatus != null;

    const handleDateAccept = (newValue) => {
        if (!newValue) return;
        const detail = {
            // Same shape as StartedDateField.jsx → updateTransitionTime.
            timestamp: `${moment(newValue).format('DD-MM-YYYY HH:mm:ss')}.0Z`,
            lead: leadId,
            id: treatmentId,
            state: treatmentStatus,
        };
        dispatch(updateTransitionTime(detail, practiceId, (res) => {
            dispatch(updateError({
                success: res?.success,
                message: res?.message || (res?.success ? 'Start date updated.' : 'Failed to update start date.'),
            }));
        }));
    };

    const goToEditPatient = () => {
        navigate(`/dashboard/practice/${practiceId}/lead-detail/${leadId}`);
        if (handleClose) handleClose();
    };

    return (
        <Box sx={{ p: 2.5 }}>
            <TreatmentContextHeader row={row} />

            {/* Change start date — collapsed accordion */}
            <Box sx={{ mt: 2, bgcolor: '#ffffff', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                <Box
                    component="button"
                    type="button"
                    data-testid="hub-change-start-date-toggle"
                    onClick={() => setShowDateEditor((v) => !v)}
                    disabled={!canEditDate}
                    sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 1.5,
                        bgcolor: 'transparent',
                        border: 'none',
                        cursor: canEditDate ? 'pointer' : 'not-allowed',
                        fontFamily: 'inherit',
                        textAlign: 'left',
                        '&:hover': canEditDate ? { bgcolor: 'rgba(0,0,0,0.02)' } : {},
                    }}
                >
                    <Iconify icon="solar:calendar-mark-bold-duotone" width={18} height={18} sx={{ color: '#078DEE', flexShrink: 0 }} />
                    <Typography sx={{ flex: 1, fontSize: '13px', fontWeight: 600, color: '#1d1d1f', letterSpacing: '-0.01em' }}>
                        Change start date
                    </Typography>
                    <Iconify icon={showDateEditor ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'} width={18} height={18} sx={{ color: '#86868b' }} />
                </Box>
                <Collapse in={showDateEditor} unmountOnExit>
                    <Box sx={{ px: 2, pb: 2, pt: 0.5 }}>
                        <Box sx={{
                            display: 'flex',
                            gap: 1,
                            p: 1.25,
                            mb: 1.5,
                            bgcolor: 'rgba(255, 149, 0, 0.10)',
                            borderLeft: '3px solid #ff9500',
                            borderRadius: '6px',
                        }}>
                            <Iconify icon="bxs:info-circle" width={16} height={16} sx={{ color: '#ff9500', mt: 0.125, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '12px', color: '#1d1d1f', letterSpacing: '-0.005em', lineHeight: 1.4, fontWeight: 600 }}>
                                Changing this affects reporting &amp; analytics for this treatment.
                            </Typography>
                        </Box>
                        <DateTimePicker
                            label="Treatment start date"
                            open={pickerOpen}
                            onOpen={() => setPickerOpen(true)}
                            onClose={() => setPickerOpen(false)}
                            value={dateValue}
                            maxDateTime={new Date()}
                            ampm={false}
                            inputFormat="dd/MM/yyyy HH:mm"
                            onChange={(v) => setDateValue(v)}
                            onAccept={handleDateAccept}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    size: 'small',
                                    onKeyDown: (e) => e.preventDefault(),
                                },
                                actionBar: { actions: ['cancel', 'accept'] },
                            }}
                        />
                    </Box>
                </Collapse>
            </Box>

            {/* Edit patient details — deep link */}
            <Stack sx={{ mt: 1.5 }}>
                <Button
                    data-testid="hub-edit-patient-btn"
                    fullWidth
                    variant="outlined"
                    startIcon={<Iconify icon="solar:user-id-bold-duotone" width={18} height={18} />}
                    onClick={goToEditPatient}
                    disabled={leadId == null}
                    sx={{
                        justifyContent: 'flex-start',
                        textTransform: 'none',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#1d1d1f',
                        borderColor: 'rgba(0,0,0,0.12)',
                        borderRadius: '12px',
                        py: 1.25,
                        '&:hover': { borderColor: 'rgba(0,0,0,0.24)', bgcolor: 'rgba(0,0,0,0.02)' },
                    }}
                >
                    Edit patient details
                </Button>
            </Stack>
        </Box>
    );
}

HubSidebarDetails.propTypes = {
    row: PropTypes.object,
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    treatmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    treatmentStatus: PropTypes.string,
    handleClose: PropTypes.func,
};

HubSidebarDetails.defaultProps = {
    row: null,
    leadId: null,
    practiceId: null,
    treatmentId: null,
    treatmentStatus: null,
    handleClose: null,
};
